import { PDFDocument } from 'pdf-lib';
import seedlogo from '../assets/seedlogo.jpg';
import { createPdfLayout, LETTER_LANDSCAPE } from './pdfLayout.mjs';
import {
  drawMonthlyReport, drawVerificationCertificate, monthlyReportHeader, certificateHeader,
  embedSignatureImages,
} from './reportPdfContent.mjs';

/**
 * The monthly report and the verification certificate, built in the browser.
 *
 * Both are drawn by pdfLayout.mjs and reportPdfContent.mjs - files the backend
 * holds identical copies of, so the certificate downloaded here and the
 * browser's fallback report are the same document as the server-signed one.
 * The report is normally the SERVER's (it alone holds the signing key); this
 * copy is only used if that download fails.
 */

/**
 * pdf-lib's standard fonts use WinAnsi (CP1252) encoding and throw on any
 * character outside it (e.g. the box-drawing "└" used for splits, smart quotes,
 * emoji), which fails the whole PDF. Sanitize every string before drawing.
 */
const winAnsiSafe = (input) => {
  let s = String(input ?? '');
  s = s
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")      // smart single quotes
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')      // smart double quotes
    .replace(/[\u2010-\u2015\u2212]/g, '-')            // hyphens, en/em dashes, minus
    .replace(/\u2026/g, '...')                          // ellipsis
    .replace(/[\u2022\u25CF\u25AA\u25A0\u25A1]/g, '*') // bullets/squares
    .replace(/[\u2500-\u257F]/g, '-')                   // box-drawing
    .replace(/[\u2190-\u21FF]/g, '-')                   // arrows
    .replace(/\u00A0/g, ' ');                           // non-breaking space
  // Drop anything still outside printable WinAnsi (keeps ASCII + Latin-1).
  return s.replace(/[^\t\n\r\x20-\x7E\xA1-\xFF]/g, '');
};

/**
 * The navbar logo's bytes - the same image as the app's header, bundled by
 * webpack. Null if it cannot be fetched; the PDF then shows the name instead.
 */
let logoCache;
const seedLogoBytes = async () => {
  if (logoCache !== undefined) return logoCache;
  try {
    const res = await fetch(seedlogo);
    logoCache = res.ok ? new Uint8Array(await res.arrayBuffer()) : null;
  } catch {
    logoCache = null;
  }
  return logoCache;
};

/**
 * The monthly report: claims, funding by PTAE, approval signatures and the
 * verification certificate.
 * @param {object} report       the monthly report with its claims
 * @param {object} certificate  the verification certificate, or null
 * @returns {Promise<Uint8Array>}
 */
export const generateMonthlyReportPDF = async (report, certificate) => {
  const pdfDoc = await PDFDocument.create();
  const layout = await createPdfLayout(pdfDoc, {
    ...monthlyReportHeader(report),
    logo: await seedLogoBytes(),
    sanitize: winAnsiSafe,
    pageSize: LETTER_LANDSCAPE,
  });
  // The signers' marks are embedded first - drawing is synchronous.
  const signatureImages = await embedSignatureImages(pdfDoc, certificate);
  drawMonthlyReport(layout, report, certificate || null, { signatureImages });
  layout.finish();
  return pdfDoc.save();
};

/**
 * The verification certificate on its own.
 * @param {object} certificate
 * @returns {Promise<Uint8Array>}
 */
export const generateVerificationPDF = async (certificate) => {
  const pdfDoc = await PDFDocument.create();
  const layout = await createPdfLayout(pdfDoc, {
    ...certificateHeader(certificate),
    logo: await seedLogoBytes(),
    sanitize: winAnsiSafe,
    pageSize: LETTER_LANDSCAPE,
  });
  drawVerificationCertificate(layout, certificate);
  layout.finish();
  return pdfDoc.save();
};

/**
 * Download the PDF to the user's device
 * @param {Uint8Array} pdfBytes - The PDF as bytes
 * @param {string} filename - The filename for download
 */
export const downloadPDF = (pdfBytes, filename) => {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const pdfUtils = { generateVerificationPDF, generateMonthlyReportPDF, downloadPDF };
export default pdfUtils;
